import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ImageRun } from 'docx';

/**
 * Convert base64 → Uint8Array
 */
export const base64ToUint8Array = (base64String) => {
  const cleanBase64 = base64String.includes('base64,')
    ? base64String.split('base64,')[1]
    : base64String;

  if (!cleanBase64 || cleanBase64.trim().length === 0) {
    throw new Error('Empty or invalid base64 string');
  }

  const binaryString = atob(cleanBase64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
};

/**
 * Filename generator
 */
const generateFileName = (address) => {
  const sanitized = (address || 'report')
    .replace(/[^a-z0-9]/gi, '_')
    .toLowerCase()
    .substring(0, 40);

  return `overlay-report_${sanitized}_${Date.now()}.docx`;
};

/**
 * Header
 */
const createDocumentHeader = (title, address) => {
  const generatedDate = new Date();

  return [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { after: 300 },
      children: [
        new TextRun({
          text: title,
          bold: true,
          size: 40,
          color: '000000',
        }),
      ],
    }),

    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 150 },
      children: [
        new TextRun({
          text: address || 'Property Address',
          size: 26,
          color: '000000',
        }),
      ],
    }),

    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: `Generated on ${generatedDate.toLocaleDateString()} at ${generatedDate.toLocaleTimeString()}`,
          size: 20,
          italics: true,
          color: '666666',
        }),
      ],
    }),
  ];
};

/**
 * Summary
 */
const createSummarySection = (count) => {
  return [
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 300, after: 150 },
      children: [
        new TextRun({
          text: 'Summary',
          bold: true,
          size: 28,
          color: '000000',
        }),
      ],
    }),

    new Paragraph({
      spacing: { after: 250 },
      children: [
        new TextRun({
          text: `This report contains ${count} overlay screenshot(s) for the property location listed above.`,
          size: 22,
          color: '000000',
        }),
      ],
    }),
  ];
};

/**
 * Screenshot Section
 */
const createScreenshotSection = (item, index, total) => {
  const sections = [];

  sections.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 300, after: 150 },
      children: [
        new TextRun({
          text: `${index + 1}. ${item.name}`,
          bold: true,
          size: 28,
          color: '000000',
        }),
      ],
    })
  );

  if (item.bytes && !item.error) {
    sections.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 120, after: 250 },
        children: [
          new ImageRun({
            data: item.bytes,
            transformation: {
              width: 7200,
              height: 5400,
            },
            type: 'image/png',
          }),
        ],
      })
    );
  } else {
    sections.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 120, after: 250 },
        children: [
          new TextRun({
            text: `[Failed to process: ${item.name}]`,
            italics: true,
            color: 'FF0000',
          }),
        ],
      })
    );
  }

  if (index < total - 1) {
    sections.push(new Paragraph({ pageBreakBefore: true }));
  }

  return sections;
};

/**
 * Process images
 */
const processScreenshots = (screenshots) => {
  return screenshots.map(({ name, screenshot }) => {
    try {
      return {
        name,
        bytes: base64ToUint8Array(screenshot),
        error: false,
      };
    } catch {
      return {
        name,
        bytes: null,
        error: true,
      };
    }
  });
};

/**
 * Build document
 */
const buildDocument = (imageData, title, address) => {
  const sections = [];

  sections.push(...createDocumentHeader(title, address));
  sections.push(...createSummarySection(imageData.length));

  imageData.forEach((item, index) => {
    sections.push(...createScreenshotSection(item, index, imageData.length));
  });

  return new Document({
    sections: [
      {
        children: sections,
      },
    ],
    styles: {
      default: {
        document: {
          run: {
            font: 'Calibri',
            size: 22,
            color: '000000',
          },
        },
      },
      paragraphStyles: [
        {
          id: 'Heading1',
          name: 'Heading 1',
          run: {
            size: 40,
            bold: true,
            font: 'Calibri',
            color: '000000',
          },
        },
        {
          id: 'Heading2',
          name: 'Heading 2',
          run: {
            size: 28,
            bold: true,
            font: 'Calibri',
            color: '000000',
          },
        },
      ],
    },
  });
};

/**
 * Download
 */
const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Main function
 */
export const generateReport = async ({ title, address, screenshots }) => {
  try {
    if (!screenshots?.length) {
      throw new Error('No screenshots provided');
    }

    const imageData = processScreenshots(screenshots);
    const doc = buildDocument(imageData, title, address);

    const blob = await Packer.toBlob(doc);
    const fileName = generateFileName(address);

    downloadBlob(blob, fileName);

    return {
      success: true,
      fileName,
      fileSize: blob.size,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message,
    };
  }
};

export default generateReport;
